<template>
  <div class="category-card">
    <!-- Category Header -->
    <div class="category-header">
      <h6 class="category-title">{{ title }}</h6>
      <b-button
        variant="outline-primary"
        size="sm"
        class="upload-btn"
        @click="$emit('upload', category)"
      >
        <i class="fas fa-plus mr-1"></i>
        อัปโหลด
      </b-button>
    </div>

    <!-- Drag and Drop Area -->
    <div 
      class="files-container"
      :class="{ 
        'drag-over': isDragOver,
        'empty-state': items.length === 0
      }"
      @dragenter.prevent="handleDragEnter"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <!-- Empty State -->
      <div v-if="items.length === 0" class="empty-state-content">
        <i class="fas fa-cloud-upload-alt empty-icon"></i>
        <p class="empty-text">
          {{ category === 'Template' ? 'ลาก PDF มาวางที่นี่ หรือ' : 'ลากไฟล์มาวางที่นี่ หรือ' }}
        </p>
        <b-button 
          variant="primary" 
          size="sm"
          @click="$emit('upload', category)"
        >
          {{ category === 'Template' ? 'เลือก PDF' : 'เลือกไฟล์' }}
        </b-button>
      </div>

      <!-- Files Grid -->
      <div v-else class="files-grid">
        <FileItem
          v-for="item in items"
          :key="item.id"
          :item="item"
          :category="category"
          :is-selected="selectedFiles.some(f => f.id === item.id)"
          @select="$emit('select', $event)"
          @preview="$emit('preview', $event)"
          @download="$emit('download', $event)"
          @delete="$emit('delete', $event)"
          @sign="$emit('sign', $event)"
          @radiiView="$emit('radiiView', $event)"
        />
      </div>

      <!-- Drag Overlay -->
      <div v-if="isDragOver" class="drag-overlay">
        <div class="drag-content">
          <i class="fas fa-cloud-upload-alt drag-icon"></i>
          <p class="drag-text">วางไฟล์ที่นี่ ({{ dragFileCount }} ไฟล์)</p>
        </div>
      </div>

      <!-- Upload Progress Overlay -->
      <div v-if="showUploadProgress" class="upload-progress-overlay">
        <div class="upload-progress-content">
          <h6 class="upload-title">กำลังอัปโหลด...</h6>
          <div class="upload-files-list">
            <div 
              v-for="uploadId in currentUploads" 
              :key="uploadId"
              class="upload-file-item"
            >
              <div class="upload-file-info">
                <i class="fas fa-file upload-file-icon"></i>
                <span class="upload-file-name">{{ getFileName(uploadId) }}</span>
                <div class="upload-status">
                  <b-spinner v-if="getFileStatus(uploadId) === 'uploading'" small></b-spinner>
                  <i v-else-if="getFileStatus(uploadId) === 'success'" class="fas fa-check-circle text-success"></i>
                  <i v-else-if="getFileStatus(uploadId) === 'error'" class="fas fa-exclamation-circle text-danger"></i>
                </div>
              </div>
              <b-progress 
                v-if="getFileStatus(uploadId) === 'uploading'"
                :value="getFileProgress(uploadId)" 
                size="sm" 
                class="upload-progress-bar"
              ></b-progress>
            </div>
          </div>
          <div class="upload-summary">
            <span class="text-muted">{{ completedUploads }} / {{ totalUploads }} เสร็จสิ้น</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FileItem from './FileItem.vue';
import ClinicFormPermissionMixin from "@/mixins/ClinicFormPermissionMixin";

export default {
  name: 'CategoryCard',
  mixins: [ClinicFormPermissionMixin],
  components: {
    FileItem
  },
  props: {
    title: {
      type: String,
      required: true
    },
    category: {
      type: String,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    selectedFiles: {
      type: Array,
      required: true
    },
    patientId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      isDragOver: false,
      dragCounter: 0,
      dragFileCount: 0,
      currentUploads: [],
      uploadService: null,
      dragLeaveTimeout: null,
    };
  },
  created() {
    // Initialize upload service
    this.uploadService = {
      uploadProgress: {},
      uploadQueue: { value: [] },
      getUploadProgress: (uploadId) => this.uploadService.uploadProgress[uploadId] || null,
      uploadFiles: this.performUpload.bind(this)
    };
  },
  computed: {
    showUploadProgress() {
      return this.currentUploads.length > 0;
    },
    completedUploads() {
      return this.currentUploads.filter(uploadId => {
        const progress = this.getUploadProgressFromService(uploadId);
        return progress && (progress.status === 'success' || progress.status === 'error');
      }).length;
    },
    totalUploads() {
      return this.currentUploads.length;
    }
  },
  methods: {
    handleDragEnter(event) {
      event.preventDefault();
      this.dragCounter++;
      
      // Clear any existing timeout
      if (this.dragLeaveTimeout) {
        clearTimeout(this.dragLeaveTimeout);
        this.dragLeaveTimeout = null;
      }
      
      if (!this.isDragOver) {
        this.isDragOver = true;
        this.dragFileCount = event.dataTransfer.items ? event.dataTransfer.items.length : 0;
      }
    },
    handleDragOver(event) {
      event.preventDefault();
      // Keep the drag state active
      if (this.dragLeaveTimeout) {
        clearTimeout(this.dragLeaveTimeout);
        this.dragLeaveTimeout = null;
      }
    },
    handleDragLeave(event) {
      event.preventDefault();
      this.dragCounter--;
      
      // Use timeout to prevent flashing when moving between child elements
      if (this.dragLeaveTimeout) {
        clearTimeout(this.dragLeaveTimeout);
      }
      
      this.dragLeaveTimeout = setTimeout(() => {
        if (this.dragCounter <= 0) {
          this.isDragOver = false;
          this.dragFileCount = 0;
          this.dragCounter = 0;
        }
      }, 50);
    },
    async handleDrop(event) {
      event.preventDefault();
      
      // Clear timeout and reset drag state
      if (this.dragLeaveTimeout) {
        clearTimeout(this.dragLeaveTimeout);
        this.dragLeaveTimeout = null;
      }
      
      this.isDragOver = false;
      this.dragCounter = 0;
      this.dragFileCount = 0;

      const files = Array.from(event.dataTransfer.files);
      if (files.length > 0) {
        // If this is a Template category, validate PDF files only
        if (this.category === 'Template') {
          if (!this.canManageClinicForm) {
            this.$emit('upload-error', {
              message: 'เพิ่มแบบฟอร์มคลินิกได้เฉพาะผู้จัดการสาขา หรือผู้บริหารระดับคลินิก (RDM / C-Level)',
              files
            });
            return;
          }
          const nonPdfFiles = files.filter(file => file.type !== 'application/pdf');
          if (nonPdfFiles.length > 0) {
            this.$emit('upload-error', {
              message: 'เฉพาะไฟล์ PDF เท่านั้นที่สามารถอัปโหลดเป็นแบบฟอร์มได้',
              files: nonPdfFiles
            });
            return;
          }
        }
        
        await this.startDirectUpload(files);
      }
    },
    async startDirectUpload(files) {
      try {
        // Show upload progress
        this.currentUploads = [];
        
        const isTemplate = this.category === 'Template';
        
        // Start upload
        await this.uploadService.uploadFiles({
          files,
          category: this.category,
          patientId: this.patientId,
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          isTemplate
        });

        // Track upload IDs
        this.currentUploads = this.uploadService.uploadQueue.value.slice();

        // Wait for completion and emit events
        this.watchUploads();

      } catch (error) {
        console.error('Upload failed:', error);
        this.$emit('upload-error', error);
      }
    },
    watchUploads() {
      const checkUploads = () => {
        const allCompleted = this.currentUploads.every(uploadId => {
          const progress = this.getUploadProgressFromService(uploadId);
          return progress && (progress.status === 'success' || progress.status === 'error');
        });

        if (allCompleted) {
          // Count successful uploads
          const successCount = this.currentUploads.filter(uploadId => {
            const progress = this.getUploadProgressFromService(uploadId);
            return progress && progress.status === 'success';
          }).length;

          if (successCount > 0) {
            // Get the uploaded files for polling
            const uploadedFiles = this.currentUploads
              .filter(uploadId => {
                const progress = this.getUploadProgressFromService(uploadId);
                return progress && progress.status === 'success';
              })
              .map(uploadId => {
                const progress = this.getUploadProgressFromService(uploadId);
                return progress.file;
              });

            // Emit success event to trigger polling/refresh
            this.$emit('upload-success', { 
              category: this.category, 
              count: successCount,
              files: uploadedFiles
            });
          }

          // Clear uploads after delay
          setTimeout(() => {
            this.currentUploads = [];
          }, 2000);
        } else {
          // Check again in 500ms
          setTimeout(checkUploads, 500);
        }
      };

      checkUploads();
    },
    async performUpload({ files, category, patientId, clinicUrl, branchUrl, isTemplate }) {
      const uploadPromises = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadId = `${category}_${Date.now()}_${i}`;
        
        // Initialize progress tracking
        this.uploadService.uploadProgress[uploadId] = {
          file: file,
          progress: 0,
          status: 'uploading',
          error: null,
          category: category
        };

        this.uploadService.uploadQueue.value.push(uploadId);

        // Create upload promise
        const uploadPromise = this.uploadSingleFile({
          file,
          category,
          patientId,
          clinicUrl,
          branchUrl,
          isTemplate,
          uploadId
        });

        uploadPromises.push(uploadPromise);
      }

      try {
        const results = await Promise.allSettled(uploadPromises);
        
        // Process results
        results.forEach((result, index) => {
          const uploadId = this.uploadService.uploadQueue.value[index];
          if (result.status === 'fulfilled') {
            this.uploadService.uploadProgress[uploadId].status = 'success';
            this.uploadService.uploadProgress[uploadId].progress = 100;
          } else {
            this.uploadService.uploadProgress[uploadId].status = 'error';
            this.uploadService.uploadProgress[uploadId].error = result.reason;
          }
        });

        return results;
      } catch (error) {
        console.error('Upload error:', error);
        throw error;
      }
    },
    async uploadSingleFile({ file, category, patientId, clinicUrl, branchUrl, isTemplate, uploadId }) {
      try {
        // Prepare upload data according to the API requirements
        const data = isTemplate ? {
          category: "Template",
          takenAt: null,
          patientId: null,
          treatmentId: null,
          labId: null,
          // ใช้ชื่อไฟล์ที่อัปโหลดเป็นชื่อแบบฟอร์ม เหมือน path ปกติด้านล่าง
          remark: this.$utils.stripFileExtension(file.name) || file.name,
          filesMetadata: [{
            contentType: file.type,
            fileSize: file.size
          }]
        } : {
          category: category,
          takenAt: null,
          patientId: patientId ? String(patientId) : null,
          treatmentId: null,
          labId: null,
          remark: file.name.split('.')[0],
          filesMetadata: [{
            contentType: file.type,
            fileSize: file.size
          }]
        };

        // Update progress
        this.uploadService.uploadProgress[uploadId].progress = 25;

        // Use the proper store action that handles presigned URLs
        const result = await this.$store.dispatch('moduleMediaFile/createMediaFiles', {
          clinicUrl,
          branchUrl,
          data,
          files: [file],
        });

        // Update progress
        this.uploadService.uploadProgress[uploadId].progress = 100;
        
        return result;
      } catch (error) {
        this.uploadService.uploadProgress[uploadId].status = 'error';
        this.uploadService.uploadProgress[uploadId].error = error.message || 'Upload failed';
        throw error;
      }
    },
    getUploadProgressFromService(uploadId) {
      return this.uploadService ? this.uploadService.getUploadProgress(uploadId) : null;
    },
    getFileName(uploadId) {
      const progress = this.getUploadProgressFromService(uploadId);
      return progress && progress.file ? progress.file.name : '';
    },
    getFileStatus(uploadId) {
      const progress = this.getUploadProgressFromService(uploadId);
      return progress ? progress.status : null;
    },
    getFileProgress(uploadId) {
      const progress = this.getUploadProgressFromService(uploadId);
      return progress ? progress.progress : 0;
    },
  },
  beforeDestroy() {
    // Clear any pending timeouts
    if (this.dragLeaveTimeout) {
      clearTimeout(this.dragLeaveTimeout);
    }
  },
};
</script>

<style scoped>
.category-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #e9ecef;
}

.category-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
}

.file-count {
  font-size: 0.8rem;
  color: #6c757d;
  background: white;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  border: 1px solid #dee2e6;
}

.upload-btn {
  border-radius: 20px;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
}

.files-container {
  position: relative;
  min-height: 120px;
  transition: all 0.3s ease;
}

.files-container.empty-state {
  border: 2px dashed #dee2e6;
  margin: 1rem;
  border-radius: 8px;
  background: #fafafa;
}

.files-container.drag-over {
  background: rgba(0, 123, 255, 0.05);
  border-color: #007bff;
}

.files-container.drag-over.empty-state {
  border-color: #007bff;
  background: rgba(0, 123, 255, 0.08);
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
}

.empty-icon {
  font-size: 2.5rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.empty-text {
  color: #6c757d;
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  padding: 1.25rem;
}

.drag-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 123, 255, 0.1);
  border: 2px dashed #007bff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.drag-content {
  text-align: center;
  padding: 2rem;
}

.drag-icon {
  font-size: 3rem;
  color: #007bff;
  margin-bottom: 1rem;
  animation: bounce 2s infinite;
}

.drag-text {
  color: #007bff;
  font-weight: 600;
  font-size: 1.1rem;
  margin: 0;
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.upload-progress-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
}

.upload-progress-content {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  max-width: 90%;
  width: 400px;
}

.upload-title {
  text-align: center;
  color: #2c3e50;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.upload-files-list {
  max-height: 200px;
  overflow-y: auto;
  margin-bottom: 1rem;
}

.upload-file-item {
  margin-bottom: 0.75rem;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.upload-file-info {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.upload-file-icon {
  color: #6c757d;
  margin-right: 0.5rem;
  width: 16px;
}

.upload-file-name {
  flex: 1;
  font-size: 0.875rem;
  color: #495057;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-status {
  margin-left: 0.5rem;
}

.upload-progress-bar {
  margin-top: 0.25rem;
}

.upload-summary {
  text-align: center;
  padding-top: 0.5rem;
  border-top: 1px solid #e9ecef;
  font-size: 0.875rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .files-grid {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.75rem;
    padding: 1rem;
  }
  
  .category-header {
    padding: 0.75rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .upload-btn {
    order: 3;
    width: 100%;
  }
}

@media (max-width: 576px) {
  .files-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 0.5rem;
    padding: 0.75rem;
  }
  
  .empty-state-content {
    padding: 1.5rem 1rem;
  }
  
  .empty-icon {
    font-size: 2rem;
  }
}
</style>
